import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2, Download, RefreshCw, Clock, Radio, Video, Archive, CheckCircle, AlertCircle, Loader, Tag, X, MessageSquare, Link, Copy, ExternalLink } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

function isLiveRecording(video) {
  return !!(video?.liveInput || video?.input?.uid || video?.live_input)
}

const CAPTION_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'nl', label: 'Dutch' },
  { code: 'pl', label: 'Polish' },
  { code: 'ru', label: 'Russian' },
  { code: 'cs', label: 'Czech' },
]

export default function VideoDetail({ video, onUpdate, onDelete, setStatus, layout = 'single', uploadSlot = null }) {
  const { api, apiKey } = useAuth()
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [creator, setCreator] = useState('')
  const [autoDelete, setAutoDelete] = useState(false)
  const [deleteAt, setDeleteAt] = useState('')
  const [playerSrc, setPlayerSrc] = useState('')
  const [downloadInfo, setDownloadInfo] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleteAfterArchive, setDeleteAfterArchive] = useState(false)

  // Tags (D1-backed via tbtv_id)
  const [tbtvId, setTbtvId] = useState(null)
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [allTags, setAllTags] = useState([])
  const [showTagSuggestions, setShowTagSuggestions] = useState(false)

  // VOD link status
  const [vodLink, setVodLink] = useState(null)
  const [vodLinkLoading, setVodLinkLoading] = useState(false)
  const [unlinking, setUnlinking] = useState(false)

  // Archive state - driven by polling the server
  const [archiveState, setArchiveState] = useState('idle')
  const [archiveMsg, setArchiveMsg] = useState('')
  const [archiveProgress, setArchiveProgress] = useState(0)
  const pollRef = useRef(null)

  // Stream Copy (live recording → uploaded copy for unrestricted clipping)
  const [copyState, setCopyState] = useState('idle') // idle | queued | preparing | copying | done | error
  const [copyMsg, setCopyMsg] = useState('')
  const [copyNewUid, setCopyNewUid] = useState(null)
  const [copyReady, setCopyReady] = useState(false)
  const copyPollRef = useRef(null)

  // Captions
  const [captions, setCaptions] = useState([])
  const [captionLang, setCaptionLang] = useState('en')
  const [captionBusy, setCaptionBusy] = useState(false)

  // Download polling
  const downloadPollRef = useRef(null)

  useEffect(() => {
    if (video) {
      setTitle(video.meta?.name || '')
      setCreator(video.creator || '')
      setAutoDelete(!!video.scheduledDeletion)
      setDeleteAt(video.scheduledDeletion 
        ? video.scheduledDeletion.replace('Z', '').substring(0, 16)
        : ''
      )
      setDeleteAfterArchive(false)
      setTags([])
      setTagInput('')
      setTbtvId(null)
      setCaptions([])
      setCaptionLang('en')
      setVodLink(null)
      // Reset stream-copy state
      if (copyPollRef.current) clearInterval(copyPollRef.current)
      setCopyState('idle')
      setCopyMsg('')
      setCopyNewUid(null)
      setCopyReady(false)

      loadPlayer()
      loadDownloadStatus()
      checkArchiveStatus()
      checkCopyStatus()
      resolveContent()
      loadAllTags()
      loadCaptions()
      loadVodLink()
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      if (downloadPollRef.current) clearInterval(downloadPollRef.current)
      if (copyPollRef.current) clearInterval(copyPollRef.current)
    }
  }, [video])

  async function loadPlayer() {
    try {
      const data = await api(`/player/${video.uid}`)
      setPlayerSrc(data.src)
    } catch (e) {
      console.error('Failed to load player:', e)
    }
  }

  async function loadDownloadStatus() {
    try {
      const data = await api(`/videos/${video.uid}/downloads`)
      let dl = null
      if (data?.result?.default) {
        dl = data.result.default
      } else if (data?.default) {
        dl = data.default
      } else if (data?.result && typeof data.result === 'object' && data.result.status) {
        dl = data.result
      } else if (data?.status) {
        dl = data
      }
      
      if (dl) {
        dl.pctComplete = dl.percentComplete ?? dl.pctComplete ?? 0
      }
      
      setDownloadInfo(dl)

      if (dl?.status === 'inprogress') {
        startDownloadPolling()
      } else {
        stopDownloadPolling()
      }
    } catch (e) {
      setDownloadInfo(null)
      stopDownloadPolling()
    }
  }

  function startDownloadPolling() {
    if (downloadPollRef.current) return
    const uid = video.uid
    downloadPollRef.current = setInterval(async () => {
      try {
        const data = await api(`/videos/${uid}/downloads`)
        let dl = null
        if (data?.result?.default) {
          dl = data.result.default
        } else if (data?.default) {
          dl = data.default
        } else if (data?.result && typeof data.result === 'object' && data.result.status) {
          dl = data.result
        } else if (data?.status) {
          dl = data
        }
        
        if (dl) {
          dl.pctComplete = dl.percentComplete ?? dl.pctComplete ?? 0
        }
        
        setDownloadInfo(dl)

        if (dl?.status !== 'inprogress') {
          stopDownloadPolling()
          if (dl?.status === 'ready') {
            setStatus('✅ Download ready')
          }
        }
      } catch {
        // keep polling on network blips
      }
    }, 5000)
  }

  function stopDownloadPolling() {
    if (downloadPollRef.current) {
      clearInterval(downloadPollRef.current)
      downloadPollRef.current = null
    }
  }

  // Resolve tbtv_id and load tags from D1
  async function resolveContent() {
    try {
      const res = await api('/content/resolve', {
        method: 'POST',
        body: JSON.stringify({
          stream_uid: video.uid,
          title: video.meta?.name || '',
          content_type: 'stream',
        }),
      })
      setTbtvId(res.tbtv_id)
      const tagRes = await api(`/tags/${res.tbtv_id}`)
      setTags(tagRes.tags || [])
    } catch {
      // D1 not configured or error
    }
  }

  // Load VOD link status for this stream UID
  async function loadVodLink() {
    setVodLinkLoading(true)
    try {
      const res = await api(`/content/vod-link/${video.uid}`)
      setVodLink(res)
    } catch {
      setVodLink(null)
    }
    setVodLinkLoading(false)
  }

  async function handleUnlinkVod() {
    if (!vodLink?.tbtv_id) return
    if (!confirm(`Clear VOD link from this stream video?\n\nThe VOD catalog entry "${vodLink.vod_title || vodLink.tbtv_id}" will NOT be deleted.`)) return

    setUnlinking(true)
    try {
      await api(`/content/vod-unlink/${video.uid}`, { method: 'POST' })
      setStatus('✅ VOD link cleared')
      setVodLink(null)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setUnlinking(false)
  }

  async function loadAllTags() {
    try {
      const data = await api('/tags')
      setAllTags(data.tags || [])
    } catch {
      // non-critical
    }
  }

  function addTag(tag) {
    const clean = tag.trim().toLowerCase()
    if (!clean || tags.includes(clean)) return
    setTags(prev => [...prev, clean])
    setTagInput('')
    setShowTagSuggestions(false)
  }

  function removeTag(tag) {
    setTags(prev => prev.filter(t => t !== tag))
  }

  function handleTagKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(tagInput)
    }
    if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    }
  }

  async function handleSaveTags() {
    if (!tbtvId) return
    setSaving(true)
    try {
      await api(`/tags/${tbtvId}`, {
        method: 'PUT',
        body: JSON.stringify({ tags }),
      })
      setStatus('✅ Tags saved')
      loadAllTags()
    } finally {
      setSaving(false)
    }
  }

  const filteredSuggestions = tagInput.length > 0
    ? allTags.filter(t => t.includes(tagInput.toLowerCase()) && !tags.includes(t))
    : []

  // Captions
  async function loadCaptions() {
    try {
      const data = await api(`/videos/${video.uid}/captions`)
      const list = data.result || data || []
      setCaptions(Array.isArray(list) ? list : [])
    } catch {
      setCaptions([])
    }
  }

  async function handleGenerateCaptions() {
    setCaptionBusy(true)
    try {
      try { await api(`/videos/${video.uid}/captions/${captionLang}`, { method: 'DELETE' }) } catch {}
      await api(`/videos/${video.uid}/captions/${captionLang}/generate`, { method: 'POST' })
      setStatus(`✅ Generating ${captionLang} captions...`)
      setTimeout(loadCaptions, 3000)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setCaptionBusy(false)
    }
  }

  async function handleDeleteCaption(lang) {
    if (!confirm(`Delete ${lang} captions?`)) return
    try {
      await api(`/videos/${video.uid}/captions/${lang}`, { method: 'DELETE' })
      setStatus(`✅ ${lang} captions deleted`)
      loadCaptions()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleSaveTitle() {
    setSaving(true)
    try {
      await onUpdate(video.uid, { title })
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveCreator() {
    setSaving(true)
    try {
      await onUpdate(video.uid, { creator })
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveAutoDelete() {
    if (!autoDelete || !deleteAt) {
      alert('Please select a date and time')
      return
    }
    
    setSaving(true)
    try {
      const deleteAtISO = new Date(deleteAt).toISOString()
      await onUpdate(video.uid, { scheduledDeletion: deleteAtISO })
    } finally {
      setSaving(false)
    }
  }

  async function handleClearAutoDelete() {
    if (!confirm('Remove scheduled deletion?')) return
    
    setSaving(true)
    try {
      await onUpdate(video.uid, { scheduledDeletion: null })
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!confirm(`Delete video "${video.meta?.name || video.uid}"?\n\nThis cannot be undone!`)) {
      return
    }
    await onDelete(video.uid)
  }

  async function handleCreateDownload() {
    try {
      setStatus('Creating download...')
      await api(`/videos/${video.uid}/downloads`, { method: 'POST' })
      setStatus('✅ Download requested — preparing...')
      setDownloadInfo({ status: 'inprogress', pctComplete: 0 })
      startDownloadPolling()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  // Check if there's already an active transfer for this video
  async function checkArchiveStatus() {
    if (pollRef.current) clearInterval(pollRef.current)
    setArchiveState('idle')
    setArchiveMsg('')
    setArchiveProgress(0)

    try {
      const result = await api(`/archive-status/${video.uid}`)
      if (result.status === 'preparing' || result.status === 'queued' || result.status === 'transferring') {
        setArchiveState(result.status)
        setArchiveMsg(result.message || `Archive ${result.status}...`)
        if (result.progress != null) setArchiveProgress(result.progress)
        startPolling()
      } else if (result.status === 'done') {
        setArchiveState('done')
        setArchiveMsg(result.message || `Archived as "${result.filename}"`)
        setArchiveProgress(100)
      } else if (result.status === 'not_found') {
        setArchiveState('not_found')
        setArchiveMsg(result.message || 'Archive marker says done but file not found in R2')
      } else if (result.status === 'error') {
        setArchiveState('error')
        setArchiveMsg(result.message || 'Archive failed')
      }
    } catch {
      // No status — idle
    }
  }

  async function checkCopyStatus() {
    if (copyPollRef.current) clearInterval(copyPollRef.current)
    try {
      const result = await api(`/stream-copy-status/${video.uid}`)
      if (result.status === 'none' || !result.status) return

      if (result.status === 'queued' || result.status === 'preparing' || result.status === 'copying') {
        setCopyState(result.status)
        setCopyMsg(result.message || `Copy ${result.status}...`)
        startCopyPolling()
      } else if (result.status === 'done') {
        setCopyState('done')
        setCopyNewUid(result.newUid || null)
        setCopyMsg(result.message || 'Copy ready')
        if (result.readyToStream) {
          setCopyReady(true)
        } else {
          // Still encoding — keep polling until readyToStream
          startCopyPolling()
        }
      } else if (result.status === 'error') {
        setCopyState('error')
        setCopyMsg(result.error || result.message || 'Copy failed')
      }
    } catch {
      // No marker or network error — leave as idle
    }
  }

  function startPolling() {
    if (pollRef.current) clearInterval(pollRef.current)
    const uid = video.uid
    pollRef.current = setInterval(async () => {
      try {
        const result = await api(`/archive-status/${uid}`)
        
        if (result.status === 'done') {
          clearInterval(pollRef.current)
          pollRef.current = null
          setArchiveState('done')
          setArchiveMsg(result.message || `Archived as "${result.filename}"`)
          setArchiveProgress(100)
          setStatus('✅ Video archived to R2')
          return
        }

        if (result.status === 'not_found') {
          clearInterval(pollRef.current)
          pollRef.current = null
          setArchiveState('not_found')
          setArchiveMsg(result.message || 'Archive marker says done but file not found in R2')
          return
        }

        if (result.status === 'error') {
          clearInterval(pollRef.current)
          pollRef.current = null
          setArchiveState('error')
          setArchiveMsg(result.message || 'Archive failed')
          return
        }

        if (result.status === 'none') {
          clearInterval(pollRef.current)
          pollRef.current = null
          setArchiveState('idle')
          setArchiveMsg('')
          return
        }

        // Still in progress — update message and real progress if available
        setArchiveState(result.status)
        setArchiveMsg(result.message || `Archive ${result.status}...`)
        if (result.progress != null) setArchiveProgress(result.progress)
      } catch {
        // Network error — keep polling
      }
    }, 5000)
  }

  async function handleArchive() {
    const videoTitle = title || video.meta?.name || 'video'
    
    if (deleteAfterArchive) {
      const ok = confirm(
        `Archive "${videoTitle}" to R2 and DELETE from Stream library?\n\nThe video will be permanently removed from Stream after archiving.`
      )
      if (!ok) return
    }

    setArchiveState('preparing')
    setArchiveMsg('Submitting archive job...')
    setArchiveProgress(0)

    try {
      const result = await api(`/archive/${video.uid}`, {
        method: 'POST',
        body: JSON.stringify({ title: videoTitle, deleteAfter: deleteAfterArchive }),
      })

      if (result.status === 'exists') {
        setArchiveState('exists')
        setArchiveMsg(`Already archived as "${result.filename}"`)
        return
      }

      if (result.status === 'error') {
        setArchiveState('error')
        setArchiveMsg(result.message || 'Failed to queue archive job')
        return
      }

      // Job queued or preparing — start polling for status
      setArchiveState(result.status)
      setArchiveMsg(result.message || 'Archive job submitted...')
      startPolling()

    } catch (e) {
      setArchiveState('error')
      setArchiveMsg(`Archive failed: ${e.message}`)
    }
  }

  async function handleVerifyArchive() {
    try {
      const result = await api(`/archive-verify/${video.uid}`)
      if (result.exists) {
        setArchiveState('done')
        setArchiveMsg(`Verified in R2: "${result.filename}" (${result.size ? (result.size / 1024 / 1024).toFixed(1) + ' MB' : 'size unknown'})`)
        setStatus('✅ File confirmed in R2')
      } else {
        setArchiveState('not_found')
        setArchiveMsg(result.message || 'File not found in R2 — marker reset')
        setStatus('⚠️ File missing from R2')
      }
    } catch (e) {
      setStatus(`Verify error: ${e.message}`)
    }
  }

  async function handleResetArchive() {
    if (!confirm('Reset the archive flag for this video?\n\nThis allows you to retry archiving. It does NOT delete any file already in R2.')) return
    try {
      await api(`/archive-reset/${video.uid}`, { method: 'POST' })
      setArchiveState('idle')
      setArchiveMsg('')
      setArchiveProgress(0)
      setStatus('Archive flag reset — ready to retry')
    } catch (e) {
      setStatus(`Reset error: ${e.message}`)
    }
  }

  async function handleStreamCopy() {
    setCopyState('queued')
    setCopyMsg('Submitting copy job...')
    setCopyNewUid(null)
    setCopyReady(false)

    try {
      const result = await api(`/stream-copy/${video.uid}`, {
        method: 'POST',
        body: JSON.stringify({ title: title || video.meta?.name }),
      })

      if (result.status === 'error' || result.error) {
        setCopyState('error')
        setCopyMsg(result.message || result.error || 'Failed to queue copy job')
        return
      }

      setCopyState(result.status || 'queued')
      setCopyMsg(result.message || 'Copy job queued...')
      startCopyPolling()
    } catch (e) {
      setCopyState('error')
      setCopyMsg(`Error: ${e.message}`)
    }
  }

  function startCopyPolling() {
    if (copyPollRef.current) clearInterval(copyPollRef.current)
    const uid = video.uid
    copyPollRef.current = setInterval(async () => {
      try {
        const result = await api(`/stream-copy-status/${uid}`)

        if (result.status === 'none') {
          clearInterval(copyPollRef.current)
          copyPollRef.current = null
          setCopyState('idle')
          return
        }

        if (result.status === 'error') {
          clearInterval(copyPollRef.current)
          copyPollRef.current = null
          setCopyState('error')
          setCopyMsg(result.error || result.message || 'Copy failed')
          return
        }

        if (result.status === 'done') {
          setCopyState('done')
          setCopyNewUid(result.newUid)
          setCopyMsg(result.message || `Copy ready — "${result.title}"`)
          if (result.readyToStream) {
            clearInterval(copyPollRef.current)
            copyPollRef.current = null
            setCopyReady(true)
            setStatus('✅ Copy ready — find it in the video list to clip')
          }
          // If not readyToStream yet, keep polling until Stream finishes encoding
          return
        }

        // Still preparing or copying
        setCopyState(result.status)
        setCopyMsg(result.message || `Copy ${result.status}...`)
      } catch {
        // Network blip — keep polling
      }
    }, 6000)
  }

  async function handleStreamCopyReset() {
    if (copyPollRef.current) clearInterval(copyPollRef.current)
    try {
      await api(`/stream-copy-reset/${video.uid}`, { method: 'POST' })
    } catch { /* best-effort */ }
    setCopyState('idle')
    setCopyMsg('')
    setCopyNewUid(null)
    setCopyReady(false)
  }

  async function handleDownload() {
    try {
      setStatus('Preparing download...')
      const API_BASE = import.meta.env.VITE_API_BASE || '/api'
      const response = await fetch(
        `${API_BASE}/download/${video.uid}?title=${encodeURIComponent(title || 'video')}`,
        { headers: { 'X-API-Key': apiKey } }
      )
      
      if (!response.ok) throw new Error('Download failed')
      
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${title || 'video'}.mp4`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      setStatus('✅ Download started')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text)
    setStatus('✅ Copied')
  }

  const hls = video.playback?.hls || video.playback?.hlsUrl
  const dash = video.playback?.dash || video.playback?.dashUrl

  const playerBlock = (
    <>
      <div className="video-player-container">
        {playerSrc ? (
          <div className="video-player-wrap">
            <iframe
              src={playerSrc}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="video-player-wrap" style={{ background: '#222', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span className="muted">Loading player...</span>
          </div>
        )}
      </div>

      {/* Processing / Restoring status banner */}
      {video.readyToStream === false && (
        <div className={`archive-status ${video.creator === 'tbtv-restore' ? 'archive-preparing' : 'archive-preparing'}`}
          style={{ marginBottom: 16, background: video.creator === 'tbtv-restore' ? 'rgba(59, 130, 246, 0.1)' : undefined,
            color: video.creator === 'tbtv-restore' ? '#3b82f6' : undefined,
            borderColor: video.creator === 'tbtv-restore' ? 'rgba(59, 130, 246, 0.2)' : undefined }}>
          <Loader size={14} className="spin" />
          {video.creator === 'tbtv-restore'
            ? `Restoring from archive — ${video.status?.state || 'processing'}...`
            : `Video is ${video.status?.state || 'processing'}...`
          }
          {video.status?.pctComplete > 0 && ` (${Math.round(video.status.pctComplete)}%)`}
        </div>
      )}

      {/* VOD Link Status Banner */}
      {vodLink?.linked && (
        <div className="vod-linked-banner">
          <Link size={14} />
          <span 
            className="vod-link-clickable"
            onClick={() => navigate(`/ondemand/catalog?select=${vodLink.tbtv_id}`)}
            title="Go to VOD in Content Catalog"
          >
            Linked to VOD: <strong>{vodLink.vod_title || vodLink.tbtv_id}</strong>
            <code>{vodLink.tbtv_id}</code>
            <ExternalLink size={10} className="vod-link-icon" />
          </span>
          {vodLink.youtube_id && (
            <span className="vod-linked-yt">YT: {vodLink.youtube_id}</span>
          )}
          <button className="btn-ghost btn-sm" onClick={() => copyToClipboard(vodLink.tbtv_id)}>
            <Copy size={12} />
          </button>
          <button
            className="btn-ghost btn-sm vod-unlink-btn"
            onClick={handleUnlinkVod}
            disabled={unlinking}
            title="Clear VOD link (does not delete VOD)"
          >
            <X size={12} /> {unlinking ? 'Clearing...' : 'Clear Link'}
          </button>
        </div>
      )}
    </>
  )

  const urlsBlock = (
    <div className="video-urls">
      <h4>Delivery URLs</h4>
      <div className="url-list">
        <div className="url-item">
          <span>UID:</span>
          <code className="mono">{video.uid}</code>
        </div>
        <div className="url-item">
          <span>Source:</span>
          <span>
            {isLiveRecording(video) ? (
              <span className="badge-live"><Radio size={10} /> Live Recording</span>
            ) : (
              <span className="badge-upload"><Video size={10} /> Uploaded</span>
            )}
          </span>
        </div>
        <div className="url-item">
          <span>Created:</span>
          <span>{new Date(video.created).toLocaleString()}</span>
        </div>
        <div className="url-item">
          <span>Duration:</span>
          <span>{video.duration ? `${Math.round(video.duration)}s` : 'N/A'}</span>
        </div>
        {hls && (
          <div className="url-item">
            <span>HLS:</span>
            <a href={hls} target="_blank" rel="noopener noreferrer" className="mono">
              {hls.substring(0, 60)}...
            </a>
            <button
              onClick={() => copyToClipboard(hls)}
              className="icon-btn"
              title="Copy HLS URL"
              style={{ marginLeft: 6 }}
            >
              <Copy size={12} />
            </button>
          </div>
        )}
        {dash && (
          <div className="url-item">
            <span>DASH:</span>
            <a href={dash} target="_blank" rel="noopener noreferrer" className="mono">
              {dash.substring(0, 60)}...
            </a>
            <button
              onClick={() => copyToClipboard(dash)}
              className="icon-btn"
              title="Copy DASH URL"
              style={{ marginLeft: 6 }}
            >
              <Copy size={12} />
            </button>
          </div>
        )}
        {downloadInfo?.status === 'ready' && (
          <div className="url-item">
            <span>MP4:</span>
            <a href={`https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${video.uid}/downloads/default.mp4`} target="_blank" rel="noopener noreferrer" className="mono">
              {`https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${video.uid}/downloads/default.mp4`.substring(0, 60)}...
            </a>
            <button
              onClick={() => copyToClipboard(`https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${video.uid}/downloads/default.mp4`)}
              className="icon-btn"
              title="Copy download URL"
              style={{ marginLeft: 6 }}
            >
              <Copy size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  )

  const editBlock = (
    <>
      <div className="video-meta-section">
        <div className="video-meta-row">
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Video title"
            />
          </div>
          <button onClick={handleSaveTitle} disabled={saving}>
            Save Title
          </button>
        </div>

        <div className="video-meta-row">
          <div className="form-group">
            <label>Creator</label>
            <input
              type="text"
              value={creator}
              onChange={e => setCreator(e.target.value)}
              placeholder="Creator name"
            />
          </div>
          <button onClick={handleSaveCreator} disabled={saving}>
            Save Creator
          </button>
        </div>

        <div className="video-tags-section">
          <label>
            <Tag size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Tags
          </label>
          <div className="tags-input-wrap">
            <div className="tags-chip-list">
              {tags.map(tag => (
                <span key={tag} className="tag-chip">
                  {tag}
                  <button className="tag-remove" onClick={() => removeTag(tag)}><X size={12} /></button>
                </span>
              ))}
              <input
                type="text"
                className="tag-text-input"
                value={tagInput}
                onChange={e => {
                  setTagInput(e.target.value)
                  setShowTagSuggestions(true)
                }}
                onKeyDown={handleTagKeyDown}
                onFocus={() => setShowTagSuggestions(true)}
                onBlur={() => setTimeout(() => setShowTagSuggestions(false), 150)}
                placeholder={tags.length === 0 ? 'Add tags...' : ''}
              />
            </div>
            {showTagSuggestions && filteredSuggestions.length > 0 && (
              <div className="tag-suggestions">
                {filteredSuggestions.map(s => (
                  <div key={s} className="tag-suggestion" onMouseDown={() => addTag(s)}>
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button onClick={handleSaveTags} disabled={saving} style={{ marginTop: 8 }}>
            Save Tags
          </button>
        </div>
      </div>
    </>
  )

  const actionsBlock = (
    <>
      <div className="download-section">
        <h4>
          <Download size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Downloads
        </h4>
        
        {downloadInfo?.status === 'ready' ? (
          <div className="download-status text-success">
            ✅ Download ready!
          </div>
        ) : downloadInfo?.status === 'inprogress' ? (
          <div className="download-status text-warning">
            ⏳ Preparing... ({downloadInfo.pctComplete || 0}%)
          </div>
        ) : downloadInfo?.status === 'error' ? (
          <div className="download-status text-danger">
            ❌ Download failed
          </div>
        ) : (
          <div className="download-status muted">
            No download available
          </div>
        )}
        
        <div className="download-actions">
          {downloadInfo?.status === 'ready' ? (
            <button onClick={handleDownload}>
              <Download size={14} /> Download MP4
            </button>
          ) : (
            <button onClick={handleCreateDownload}>
              Request Download
            </button>
          )}
          <button onClick={loadDownloadStatus} className="secondary">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="auto-delete-section">
        <h4>
          <MessageSquare size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          AI Captions
        </h4>

        {captions.length > 0 && (
          <div className="captions-list">
            {captions.map(cap => (
              <div key={cap.language} className="caption-item">
                <span className="caption-lang">{cap.label || cap.language}</span>
                <span className={`caption-status caption-status-${cap.status}`}>
                  {cap.status === 'ready' ? '✅ Ready' :
                   cap.status === 'inprogress' ? '⏳ Generating...' :
                   cap.status === 'error' ? '❌ Error' : cap.status}
                </span>
                {cap.generated && <span className="caption-badge">AI</span>}
                <button
                  onClick={() => handleDeleteCaption(cap.language)}
                  className="caption-delete"
                  title="Delete caption"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {captions.length === 0 && (
          <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>No captions yet</div>
        )}

        <div className="caption-generate-row">
          <select
            value={captionLang}
            onChange={e => setCaptionLang(e.target.value)}
            className="caption-lang-select"
          >
            {CAPTION_LANGUAGES.map(l => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>
          <button onClick={handleGenerateCaptions} disabled={captionBusy}>
            {captionBusy ? 'Generating...' : 'Generate Captions'}
          </button>
          <button onClick={loadCaptions} className="secondary">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="auto-delete-section">
        <h4>
          <Clock size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Auto-Delete Schedule
        </h4>
        
        <label className="auto-delete-toggle">
          <input
            type="checkbox"
            checked={autoDelete}
            onChange={e => setAutoDelete(e.target.checked)}
          />
          Schedule auto-delete
        </label>
        
        {autoDelete && (
          <div className="auto-delete-controls">
            <input
              type="datetime-local"
              value={deleteAt}
              onChange={e => setDeleteAt(e.target.value)}
            />
            <button onClick={handleSaveAutoDelete} disabled={saving}>
              Save Schedule
            </button>
            <button onClick={handleClearAutoDelete} disabled={saving} className="secondary">
              Clear
            </button>
          </div>
        )}
        
        {video.scheduledDeletion && (
          <div className="auto-delete-status text-warning">
            ⏰ Scheduled: {new Date(video.scheduledDeletion).toLocaleString()}
          </div>
        )}
      </div>

      <div className="archive-section">
        <h4>
          <Archive size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Archive to R2
        </h4>

        <p className="archive-description">
          Create an MP4 and store it in the R2 archive bucket. 
          Optionally delete the video from Stream after archiving.
        </p>

        <label className="archive-delete-toggle">
          <input
            type="checkbox"
            checked={deleteAfterArchive}
            onChange={e => setDeleteAfterArchive(e.target.checked)}
            disabled={archiveState !== 'idle' && archiveState !== 'done' && archiveState !== 'exists' && archiveState !== 'error'}
          />
          Delete from Stream after archive
        </label>

        {archiveState === 'idle' && (
          <button onClick={handleArchive} className="archive-btn">
            <Archive size={14} /> Archive Video
          </button>
        )}

        {(archiveState === 'preparing' || archiveState === 'queued' || archiveState === 'transferring' || archiveState === 'in_progress') && (
          <div className="archive-progress-area">
            <div className="archive-status archive-preparing">
              <Loader size={14} className="spin" /> {archiveMsg}
            </div>
            <div className="archive-progress-bar">
              {archiveProgress > 0
                ? <div className="archive-progress-fill" style={{ width: `${archiveProgress}%` }} />
                : <div className="archive-progress-fill archive-progress-indeterminate" />
              }
            </div>
          </div>
        )}

        {archiveState === 'done' && (
          <>
            <div className="archive-status archive-done">
              <CheckCircle size={14} /> {archiveMsg}
            </div>
            <div className="archive-verify-row">
              <button onClick={handleVerifyArchive} className="archive-btn secondary">
                Verify in R2
              </button>
              <button onClick={handleResetArchive} className="archive-btn secondary">
                Reset &amp; Retry
              </button>
            </div>
          </>
        )}

        {archiveState === 'exists' && (
          <>
            <div className="archive-status archive-exists">
              <AlertCircle size={14} /> {archiveMsg}
            </div>
            <div className="archive-verify-row">
              <button onClick={handleVerifyArchive} className="archive-btn secondary">
                Verify in R2
              </button>
              <button onClick={handleResetArchive} className="archive-btn secondary">
                Reset &amp; Retry
              </button>
            </div>
          </>
        )}

        {archiveState === 'not_found' && (
          <>
            <div className="archive-status archive-error">
              <AlertCircle size={14} /> {archiveMsg}
            </div>
            <div className="archive-verify-row">
              <button onClick={handleResetArchive} className="archive-btn secondary">
                Reset Flag
              </button>
              <button onClick={handleArchive} className="archive-btn">
                <Archive size={14} /> Retry Archive
              </button>
            </div>
          </>
        )}

        {archiveState === 'error' && (
          <>
            <div className="archive-status archive-error">
              <AlertCircle size={14} /> {archiveMsg}
            </div>
            <button onClick={handleArchive} className="archive-btn">
              Retry Archive
            </button>
          </>
        )}
      </div>

      {isLiveRecording(video) && (
        <div className="archive-section">
          <h4>
            <Copy size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Convert to Uploaded Copy
          </h4>

          <p className="archive-description">
            Creates a new uploaded video named <strong>RESTORE- {title || video.meta?.name || '...'}</strong>.
            Once ready, find it in the video list and use Stream Editor to clip at any duration.
          </p>

          {copyState === 'idle' && (
            <button onClick={handleStreamCopy} className="archive-btn">
              <Copy size={14} /> Convert to Uploaded Copy
            </button>
          )}

          {(copyState === 'queued' || copyState === 'preparing' || copyState === 'copying') && (
            <div className="archive-progress-area">
              <div className="archive-status archive-preparing">
                <Loader size={14} className="spin" /> {copyMsg}
              </div>
              <div className="archive-progress-bar">
                <div className="archive-progress-fill archive-progress-indeterminate" />
              </div>
            </div>
          )}

          {copyState === 'done' && !copyReady && (
            <div className="archive-status archive-preparing">
              <Loader size={14} className="spin" /> {copyMsg}
            </div>
          )}

          {copyState === 'done' && copyReady && (
            <>
              <div className="archive-status archive-done">
                <CheckCircle size={14} /> {copyMsg}
              </div>
              {copyNewUid && (
                <p className="archive-description" style={{ marginTop: 6 }}>
                  UID: <span className="mono" style={{ fontSize: 11 }}>{copyNewUid}</span>
                </p>
              )}
            </>
          )}

          {copyState === 'error' && (
            <>
              <div className="archive-status archive-error">
                <AlertCircle size={14} /> {copyMsg}
              </div>
              <div className="archive-verify-row">
                <button onClick={handleStreamCopyReset} className="archive-btn secondary">
                  Reset
                </button>
                <button onClick={handleStreamCopy} className="archive-btn">
                  <Copy size={14} /> Retry
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="video-actions">
        <button onClick={handleDelete} className="danger">
          <Trash2 size={14} /> Delete Video
        </button>
      </div>
    </>
  )

  return (
    <div className="video-detail">
      {layout === 'split' ? (
        <div className="video-detail-split">
          <div className="video-detail-split-left">
            {playerBlock}
          </div>
          <div className="video-detail-split-right">
            {editBlock}
            {urlsBlock}
            {actionsBlock}
            {uploadSlot}
          </div>
        </div>
      ) : (
        <>
          {playerBlock}
          {editBlock}
          {urlsBlock}
          {actionsBlock}
        </>
      )}
    </div>
  )
}
